import { ClipboardList, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CareTimeEditor } from './NoteTimingMeta';
import { getHandoverPatients } from '../utils/handoverUtils';
import {
  getHandoverEntryBody,
  getHandoverNoteBody,
  setHandoverEntryBody,
  setHandoverNoteBody,
} from '../utils/handoverNoteBody';

function HandoverTextArea({ value, onChange, readOnly, placeholder, ariaLabel }) {
  return (
    <textarea
      value={value}
      readOnly={readOnly}
      disabled={readOnly}
      placeholder={placeholder}
      rows={10}
      onChange={(event) => {
        if (readOnly) return;
        onChange(event.target.value);
      }}
      className="w-full resize-y rounded-xl border-0 bg-gray-100 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
      aria-label={ariaLabel}
      dir="auto"
    />
  );
}

function NoteTypeRow({ label, onDelete = null, deleteLabel }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <ClipboardList className="h-5 w-5 shrink-0 text-gray-900" strokeWidth={2} />
        <h2 className="text-base font-bold text-gray-900">{label}</h2>
      </div>
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-primary"
          aria-label={deleteLabel}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
}

export default function SbarNote({
  note,
  onChange,
  onDelete = null,
  readOnly = false,
  showTypeHeader = true,
  careTime = null,
  careDayOffset = null,
  documentationTime = null,
  onCareTimeChange = null,
}) {
  const { t } = useLanguage();
  const patients = getHandoverPatients(note);
  const multi = patients.length > 1;
  const typeLabel = t('shiftHandover');
  const showPerPatientTiming = Boolean(multi && onCareTimeChange);

  const handleSingleChange = (text) => {
    onChange(setHandoverNoteBody(note, text));
  };

  const handlePatientChange = (index, text) => {
    const nextPatients = patients.map((entry, i) =>
      i === index ? setHandoverEntryBody(entry, text) : entry
    );
    onChange({
      ...note,
      note_type: 'handover',
      patients: nextPatients,
      ...(nextPatients.length === 1
        ? {
            narrative: text,
            sbar: {
              situation: text,
              background: '',
              assessment: '',
              recommendation: '',
            },
          }
        : {}),
    });
  };

  return (
    <div className="flex flex-col gap-2.5">
      {!multi && showTypeHeader ? (
        <NoteTypeRow
          label={typeLabel}
          onDelete={onDelete}
          deleteLabel={t('removeNote')}
        />
      ) : null}

      {multi ? (
        patients.map((entry, index) => {
          const displayName = entry.patient_name || `Patient ${index + 1}`;
          return (
            <div
              key={`handover-patient-${index}`}
              id={`handover-patient-${index}`}
              className={`scroll-mt-3 space-y-2 ${index > 0 ? 'mt-4 border-t border-gray-100 pt-4' : ''}`}
            >
              <div>
                <p className="font-bold text-gray-900" dir="auto">
                  {displayName}
                </p>
                {entry.room ? (
                  <p className="text-sm text-gray-500">
                    {t('room')} {entry.room}
                  </p>
                ) : null}
              </div>
              {showTypeHeader ? (
                <NoteTypeRow
                  label={typeLabel}
                  onDelete={index === 0 ? onDelete : null}
                  deleteLabel={t('removeNote')}
                />
              ) : null}
              {showPerPatientTiming ? (
                <CareTimeEditor
                  careTime={careTime}
                  careDayOffset={careDayOffset}
                  documentationTime={documentationTime}
                  onChange={onCareTimeChange}
                />
              ) : null}
              <HandoverTextArea
                value={getHandoverEntryBody(entry)}
                onChange={(text) => handlePatientChange(index, text)}
                readOnly={readOnly}
                placeholder={t('progressNotePlaceholder')}
                ariaLabel={`${typeLabel} — ${displayName}`}
              />
            </div>
          );
        })
      ) : (
        <HandoverTextArea
          value={getHandoverNoteBody(note)}
          onChange={handleSingleChange}
          readOnly={readOnly}
          placeholder={t('progressNotePlaceholder')}
          ariaLabel={typeLabel}
        />
      )}
    </div>
  );
}
